/**
 * WorkspaceList Component
 * Displays list of user's workspaces with create option
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  FolderIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { workspacesAPI, type Workspace } from '@/api/workspaces'
import { logError } from '@utils/logger'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { ConfirmDialog } from '@components/common/ConfirmDialog'

interface WorkspaceListProps {
  onCreateWorkspace: () => void
  onSelectWorkspace: (workspaceId: string) => void
  refreshTrigger?: number // Trigger refresh when this changes
}

export const WorkspaceList: React.FC<WorkspaceListProps> = ({
  onCreateWorkspace,
  onSelectWorkspace,
  refreshTrigger,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  useEffect(() => {
    loadWorkspaces()
  }, [refreshTrigger])

  const loadWorkspaces = async () => {
    setIsLoading(true)
    try {
      const data = await workspacesAPI.listWorkspaces()
      setWorkspaces(data || [])
    } catch (error) {
      logError('Error loading workspaces:', error)
      toast.error('Failed to load workspaces')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteClick = (e: React.MouseEvent, workspaceId: string) => {
    e.stopPropagation() // Prevent card click
    setDeleteTarget(workspaceId)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    
    try {
      await workspacesAPI.deleteWorkspace(deleteTarget)
      toast.success('Workspace deleted')
      loadWorkspaces() // Refresh list
      setDeleteTarget(null)
    } catch (error) {
      logError('Error deleting workspace:', error)
      toast.error('Failed to delete workspace')
      setDeleteTarget(null)
    }
  }

  const filteredWorkspaces = workspaces.filter(ws => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      ws.title?.toLowerCase().includes(query) ||
      ws.query.toLowerCase().includes(query) ||
      ws.field?.toLowerCase().includes(query)
    )
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading workspaces...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Research Workspaces
        </h2>
        <button
          onClick={onCreateWorkspace}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          New Workspace
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Workspaces List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredWorkspaces.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchQuery
                ? 'No workspaces match your search'
                : 'No workspaces yet. Create your first workspace to start researching.'}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateWorkspace}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
              >
                Create Workspace
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredWorkspaces.map((workspace) => (
              <div
                key={workspace.workspace_id}
                onClick={() => onSelectWorkspace(workspace.workspace_id)}
                className={clsx(
                  'p-4 rounded-lg border cursor-pointer transition-all hover:shadow-lg relative',
                  'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800',
                  'hover:border-cyan-300 dark:hover:border-cyan-700'
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <FolderIcon className="w-6 h-6 text-cyan-600 dark:text-cyan-400 flex-shrink-0 mt-1" />
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'px-2 py-1 text-xs rounded',
                        workspace.status === 'completed'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : workspace.status === 'retrieving'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {workspace.status}
                    </span>
                    <button
                      onClick={(e) => handleDeleteClick(e, workspace.workspace_id)}
                      className="p-1 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Delete workspace"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1 line-clamp-2">
                  {workspace.title || workspace.query.substring(0, 50) + '...'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                  {workspace.query}
                </p>
                {workspace.field && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Field: {workspace.field}
                  </p>
                )}
                {workspace.created_at && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <ClockIcon className="w-4 h-4" />
                    {new Date(workspace.created_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Delete Workspace"
        message="Are you sure you want to delete this workspace? This action cannot be undone. All associated data (papers, evidence, syntheses, and chat messages) will be permanently deleted."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

